import { z } from 'zod';
import { Action } from './actions.js';
import { Uuid } from './common.js';
import { R1Detail } from './r1.js';

/**
 * Lot AW « Hors connexion sur mobile » : contrat des gestes internes gardés sur le téléphone puis rejoués au
 * retour du réseau.
 *
 * - `Idempotency-Key` (uuid créé par le téléphone, un par changement) : une requête rejouée avec la même clé reçoit
 *   la réponse enregistrée la première fois (en-tête `Idempotent-Replayed: true`) au lieu de refaire le geste.
 *   Seules les routes internes de la liste `IDEMPOTENT_ROUTES` (serveur) le lisent ; ailleurs il est ignoré.
 * - `X-Base-Updated-At` : la version de la carte (ou des infos du R1) que la personne avait sous les yeux. Si la
 *   ligne a changé depuis, le serveur répond 409 `ACTION_CHANGED` (ou `R1_PREP_CHANGED`) avec la version actuelle
 *   et la personne qui l'a changée : le téléphone compare champ par champ, rien n'est écrasé sans choix explicite.
 *   Valeur vide = « jamais modifiée » (`updatedAt` null).
 */
export const IDEMPOTENCY_KEY_HEADER = 'idempotency-key';
export const BASE_UPDATED_AT_HEADER = 'x-base-updated-at';
export const IDEMPOTENT_REPLAYED_HEADER = 'idempotent-replayed';

/** Clé d'idempotence acceptée : uuid, éventuellement suivi d'un suffixe (`<uuid>:plan`, `<uuid>:att:0`). */
export const IdempotencyKey = z.string().regex(/^[0-9a-f-]{36}(?::[a-z0-9:_-]{1,40})?$/i);

/** Qui a changé la ligne en dernier (journal d'audit ou auteur des infos du R1), null si inconnu. */
export const ChangedBy = z.object({ id: Uuid, name: z.string() }).nullable();
export type ChangedBy = z.infer<typeof ChangedBy>;

/** `details` du 409 `ACTION_CHANGED` (`PATCH /api/actions/:id` avec `X-Base-Updated-At`). */
export const ActionChangedDetails = z.object({ current: Action, changedBy: ChangedBy });
export type ActionChangedDetails = z.infer<typeof ActionChangedDetails>;

/** `details` du 409 `R1_PREP_CHANGED` (`PUT /api/r1/:r1Key/prep` avec `X-Base-Updated-At`). */
export const R1PrepChangedDetails = z.object({ current: R1Detail, changedBy: ChangedBy });
export type R1PrepChangedDetails = z.infer<typeof R1PrepChangedDetails>;
